import { Link } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { ADMIN_GROUPS, ADMIN_SECTIONS, canSeeAdminSection } from "../nav";
import type { AdminSectionId } from "../nav";

/**
 * Club console section navigation. The approved design renders these groups
 * as one horizontal bar; the component name remains for import compatibility.
 * Items are hidden per `canSeeAdminSection`, so a team admin sees People +
 * Match Library and a club-admin-level user sees everything.
 * Platform tooling lives on the super-admin console, not here.
 *
 * The active section is highlighted via `data-active`; the data
 * attribute lets E2E + screenshot tests assert without scraping
 * className strings.
 */
export function AdminSidebar({
  active,
}: {
  active: AdminSectionId;
}) {
  const me = useMe().data;

  return (
    <aside
      data-testid="admin-sidebar"
      className="w-full shrink-0 border-b border-rule bg-surface px-2 py-2 shadow-sm md:w-56 md:border-b-0 md:border-r md:py-3 md:shadow-none"
    >
      <nav className="flex flex-wrap gap-3 overflow-visible pb-1 md:flex-col md:gap-4 md:pb-0">
        {ADMIN_GROUPS.map((group) => {
          const items = ADMIN_SECTIONS.filter(
            (s) => s.group === group.id && canSeeAdminSection(s.id, me),
          );
          if (items.length === 0) return null;

          return (
            <div
              key={group.id}
              className="min-w-max md:min-w-0"
              data-admin-group={group.id}
            >
              <h3 className="px-2 mb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                {group.label}
              </h3>
              <ul className="flex gap-1 md:flex-col md:gap-0.5">
                {items.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.id}>
                      <Link
                        to={s.path}
                        data-testid={`admin-nav-${s.id}`}
                        data-admin-section={s.id}
                        data-active={s.id === active ? "true" : undefined}
                        className="group flex min-w-[8.5rem] items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink md:min-w-0"
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        <span className="flex flex-col leading-tight">
                          <span className="text-sm">{s.label}</span>
                          <span className="text-[0.6rem] text-current opacity-70">
                            {s.kicker}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
